<template>
  <RequestWizardStep name="Contact Info" :complete="isComplete">
    <VeeValidateWrapper v-if="is3rdParty"
      @validation-failed="onBehalfFormIsValid = false"
      @validation-passed="onBehalfFormIsValid = true"
      slim
    >
      <ContentCard :title="title">
        <p class="lead">Please fill out the necessary contact information for the person
          whose records you are requesting.</p>

        <CompleteFormMessage class="mb-4" />

        <DynamicForm
          :fields="onBehalfFields"
          class="row g-3"
          v-model="model"
        />
      </ContentCard>
    </VeeValidateWrapper>

    <VeeValidateWrapper
      @validation-failed="fromFormIsValid = false"
      @validation-passed="fromFormIsValid = true"
      slim
    >
      <ContentCard :title="!is3rdParty ? title : null">
        <template v-if="is3rdParty">
          <p class="lead">
            We also need you to enter information for the individual and/or
            organization who will be receiving the requested records. This individual will
            serve as the point of contact if any questions should arise while fulfilling the
            request.
          </p>
        </template>
        <template v-else>
          <p class="lead">
            Please tell us a little bit more about yourself:
          </p>

          <CompleteFormMessage class="mb-2" />
        </template>

        <p class="fst-italic text-muted mb-4">
          We encourage you to include an email address and phone number for quicker and
          easier communication with the HRSA FOIA office.
        </p>

        <DynamicForm
          :fields="fromFields"
          class="row g-3"
          v-model="model"
        />
      </ContentCard>
    </VeeValidateWrapper>
  </RequestWizardStep>
</template>

<script>
import DynamicForm from '@ssi/vue-common/src/components/formControls/DynamicForm';
import CompleteFormMessage from '@/components/formFields/CompleteFormMessage';
import VeeValidateWrapper from '@/components/VeeValidateWrapper';
import RequestWizardStep from '@/components/requestWizard/RequestWizardStep';
import {
  firstName, lastName, organization, address1, address2,
  city, state, zip, email, phone, fax
} from './fields';

const modelEvent = 'update';
const modelProp = 'value';

export default {
  model: {
    prop: modelProp,
    event: modelEvent
  },

  components: {
    CompleteFormMessage, DynamicForm, RequestWizardStep, VeeValidateWrapper
  },

  props: {
    [modelProp]: {
      type: Object,
      required: true
    },

    is3rdParty: {
      type: Boolean,
      default: () => false
    }
  },

  data() {
    return {
      fromFormIsValid: false,
      onBehalfFormIsValid: false
    };
  },

  computed: {
    isComplete() {
      return this.fromFormIsValid && (this.onBehalfFormIsValid || !this.is3rdParty);
    },

    model: {
      get() { return this[modelProp]; },
      set(value) { this.$emit(modelEvent, value); }
    },

    fromFields: () => [
      { ...firstName, name: 'fromFirstName' },
      { ...lastName, name: 'fromLastName' },
      { ...organization, name: 'fromOrganization' },
      { ...address1, name: 'fromAddressLine1' },
      { ...address2, name: 'fromAddressLine2' },
      { ...city, name: 'fromCity' },
      { ...state, name: 'fromState' },
      { ...zip, name: 'fromZip' },
      // Use aggressive validation for the last required field so that the "Next" button is enabled
      // as soon as the form is valid rather than forcing the user to leave the field first.
      { ...phone, name: 'fromPhone', validationAttrs: { mode: 'aggressive' } },
      { ...email, name: 'fromEmail' },
      { ...fax, name: 'fromFax', }
    ],

    onBehalfFields: () => [
      { ...firstName, name: 'onBehalfOfFirstName' },
      { ...lastName, name: 'onBehalfOfLastName' },
      { ...address1, name: 'onBehalfOfAddressLine1' },
      { ...address2, name: 'onBehalfOfAddressLine2' },
      { ...city, name: 'onBehalfOfCity' },
      { ...state, name: 'onBehalfOfState' },
      { ...zip, name: 'onBehalfOfZip' }
    ]
  },

  created() {
    this.title = 'Great! We can help process your request.';
  }
};
</script>
